import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import AnimatedMetric from "./AnimatedMetric";
import DemoBadge from "./DemoBadge";
import PulseDot from "./PulseDot";
import { Users, DollarSign } from "lucide-react";

export const CitizenImpactPanel = ({ entity = "City of Cedar Falls" }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/engine/citizen-impact", { params: { entity_name: entity } })
      .then(r => setD(r.data)).catch(() => {});
  }, [entity]);

  if (!d) return <div className="glass p-6 animate-pulse"><div className="h-40"/></div>;

  return (
    <div className="glass p-0 overflow-hidden" data-testid="citizen-impact-panel">
      <div className="flex items-center justify-between p-5 border-b border-gold-dim/20">
        <div>
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.24em] text-electric">
            <PulseDot /> Citizen Impact · Reallocation Savings
          </div>
          <h3 className="font-display text-xl mt-1">{d.entity_name}</h3>
        </div>
        <div className="text-right">
          <DemoBadge />
          <div className="text-[10px] uppercase tracking-[0.22em] text-steel mt-1">
            <Users size={10} className="inline mr-1" /> {d.households.toLocaleString()} households
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4">
        <Cell label="Before / month" value={d.before_monthly_usd_per_household} prefix="$" tone="warning" />
        <Cell label="After / month"  value={d.after_monthly_usd_per_household}  prefix="$" tone="success" />
        <Cell label="Savings / household · month" value={d.monthly_savings_usd_per_household}
              prefix="$" tone="gold" highlight />
        <Cell label="Savings / household · year"  value={d.annual_savings_usd_per_household}
              prefix="$" tone="gold" />
      </div>
      <div className="p-5 border-t border-gold-dim/15 grid md:grid-cols-2 gap-3">
        <div className="panel p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-steel flex items-center gap-2">
            <DollarSign size={12} /> Community total · monthly
          </div>
          <div className="metric text-2xl text-cream mt-1">
            <AnimatedMetric value={d.monthly_savings_total_usd} prefix="$" decimals={0} testid="impact-monthly-total" />
          </div>
        </div>
        <div className="panel p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-steel flex items-center gap-2">
            <DollarSign size={12} /> Community total · annual
          </div>
          <div className="metric text-2xl text-gold mt-1">
            <AnimatedMetric value={d.annual_savings_total_usd} prefix="$" decimals={0} testid="impact-annual-total" />
          </div>
        </div>
      </div>
      <div className="px-5 pb-5 text-xs text-cream-dim">
        Reallocation modelled across: <span className="text-gold">{d.infrastructure_areas.join(" · ")}</span>
      </div>
    </div>
  );
};

const Cell = ({ label, value, prefix = "", tone = "cream", highlight = false }) => {
  const color = tone === "warning" ? "text-[color:var(--auditiq-warning)]" :
                tone === "success" ? "text-[color:var(--auditiq-success)]" :
                tone === "gold"    ? "text-gold" : "text-cream";
  return (
    <div className={`p-5 border-r border-gold-dim/10 ${highlight ? "bg-[rgba(201,168,76,0.05)]" : ""}`}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-steel">{label}</div>
      <div className={`metric text-3xl mt-1 ${color}`}>
        <AnimatedMetric value={value} prefix={prefix} decimals={0} />
      </div>
    </div>
  );
};

export default CitizenImpactPanel;
